import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import {
  PaymentLink,
  PaymentStatus,
  PaymentAttempt,
  Payment,
  CreatePaymentLinkInput,
  CreateSplitPaymentLinkInput,
  AttemptStatus,
  AddManualPaymentInput,
  EditManualPaymentInput,
  SoftDeleteManualPaymentInput,
  ReconcileProcessingPaymentInput,
  ProcessingReconciliationDetails,
} from '../types/PaymentLink';
import { generateLinkId } from '../utils/generateLinkId';
import { cleanFirestoreData } from '../utils/firestoreHelpers';
import { buildPaymentUrl } from '../config/urls';

const PAYMENT_LINKS_COLLECTION = 'paymentLinks';
const PAYMENT_ATTEMPTS_COLLECTION = 'paymentAttempts';

/**
 * Creates a new payment link
 */
export const createPaymentLink = async (input: CreatePaymentLinkInput): Promise<PaymentLink> => {
  try {
    // Generate unique link ID (only for 'link' type, not for 'guest')
    const linkId = input.type === 'guest' ? `GUEST-${Date.now()}` : await generateLinkId();
    const linkUrl = input.type === 'guest'
      ? buildPaymentUrl(`/guest-payment/${linkId}`)
      : buildPaymentUrl(`/pay/${linkId}`);

    // Safety net: the deposit deadline must never be later than the invoice
    // deadline. Default a missing deposit due date to the invoice due date (same day).
    const safeDepositDueDate =
      input.depositDueDate && input.depositDueDate.getTime() <= input.invoiceDueDate.getTime()
        ? input.depositDueDate
        : input.invoiceDueDate;

    // Prepare payment link data with new invoice structure
    const paymentLinkData = {
      linkId,
      type: input.type,
      customerName: input.customerName,
      customerEmail: input.customerEmail,
      customerPhone: input.customerPhone,
      // Only include payer fields if they have values (for guest payments)
      ...(input.payerFirstName && { payerFirstName: input.payerFirstName }),
      ...(input.payerLastName && { payerLastName: input.payerLastName }),
      // Only include invoice number if provided
      ...(input.invoiceNumber && { invoiceNumber: input.invoiceNumber }),
      invoiceAmount: input.invoiceAmount,
      depositAmount: input.depositAmount,
      currency: input.currency,
      description: input.description,
      depositDueDate: Timestamp.fromDate(safeDepositDueDate),
      invoiceDueDate: Timestamp.fromDate(input.invoiceDueDate),
      totalPaid: 0,
      remainingAmount: input.invoiceAmount,
      depositPaid: false,
      invoicePaid: false,
      allowPartialPayment: input.allowPartialPayment,
      payments: [],
      linkUrl,
      expiryDate: Timestamp.fromDate(new Date(input.invoiceDueDate.getFullYear(), input.invoiceDueDate.getMonth(), input.invoiceDueDate.getDate(), 23, 59, 59, 999)), // End of invoice due date
      status: 'pending' as PaymentStatus,
      visitCount: 0,
      createdBy: input.createdBy,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      // Backward compatibility
      amount: input.invoiceAmount,
      // Split payment fields (for standard links, these are undefined/null)
      isSplitPayment: false,
      splitParentId: null,
    };

    // Clean data to remove any undefined values before saving to Firestore
    const cleanData = cleanFirestoreData(paymentLinkData);

    // Add to Firestore
    const docRef = await addDoc(collection(db, PAYMENT_LINKS_COLLECTION), cleanData);

    return {
      id: docRef.id,
      ...paymentLinkData,
    };
  } catch (error) {
    console.error('Error creating payment link:', error);
    throw error;
  }
};

/**
 * Creates a new GUEST payment link by calling the cloud function
 */
export const createGuestPaymentLink = async (input: CreatePaymentLinkInput): Promise<PaymentLink> => {
  try {
    const functions = getFunctions();
    const createGuestLink = httpsCallable(functions, 'createGuestPaymentLink');

    // The input object for the cloud function should be serializable.
    // Convert Timestamps to ISO strings or milliseconds if they exist.
    const serializableInput = {
      ...input,
      depositDueDate: input.depositDueDate.toISOString(),
      invoiceDueDate: input.invoiceDueDate.toISOString(),
    };

    const result = await createGuestLink(serializableInput);
    
    // Define an interface for the raw response to avoid using 'any'
    interface RawPaymentLinkResponse extends Omit<PaymentLink, 'createdAt' | 'updatedAt' | 'depositDueDate' | 'invoiceDueDate' | 'expiryDate' | 'payments'> {
      createdAt: { _seconds: number; _nanoseconds: number };
      updatedAt: { _seconds: number; _nanoseconds: number };
      depositDueDate: { _seconds: number; _nanoseconds: number };
      invoiceDueDate: { _seconds: number; _nanoseconds: number };
      expiryDate: { _seconds: number; _nanoseconds: number };
      payments: Payment[];
    }

    const newLink = result.data as RawPaymentLinkResponse;

    // The cloud function returns data that needs to be converted back to the frontend types,
    // specifically converting server timestamps to client Timestamps.
    return {
      ...newLink,
      createdAt: new Timestamp(newLink.createdAt._seconds, newLink.createdAt._nanoseconds),
      updatedAt: new Timestamp(newLink.updatedAt._seconds, newLink.updatedAt._nanoseconds),
      depositDueDate: new Timestamp(newLink.depositDueDate._seconds, newLink.depositDueDate._nanoseconds),
      invoiceDueDate: new Timestamp(newLink.invoiceDueDate._seconds, newLink.invoiceDueDate._nanoseconds),
      expiryDate: new Timestamp(newLink.expiryDate._seconds, newLink.expiryDate._nanoseconds),
    } as PaymentLink;

  } catch (error) {
    console.error('Error calling createGuestPaymentLink cloud function:', error);
    throw error;
  }
};

/**
 * Creates split payment links by calling the cloud function
 */
export const createSplitPaymentLinks = async (input: CreateSplitPaymentLinkInput): Promise<any> => {
  try {
    const functions = getFunctions();
    const createSplitLinks = httpsCallable(functions, 'createSplitPaymentLinks');

    // Convert Timestamps to ISO strings for serialization
    const serializableInput = {
      ...input,
      invoiceDueDate: input.invoiceDueDate.toISOString(),
    };

    const result = await createSplitLinks(serializableInput);

    return result.data;
  } catch (error) {
    console.error('Error calling createSplitPaymentLinks cloud function:', error);
    throw error;
  }
};

/**
 * Gets a payment link by linkId (supports both old and new formats)
 * Old format: ABC12345 or random 12-char
 * New format: ALX-YYMMDDHHMM-XXXXX or ALX-YYMMDDHHMM-XXXXX-P1
 */
export const getPaymentLinkByLinkId = async (linkId: string): Promise<PaymentLink | null> => {
  try {
    console.log('[getPaymentLinkByLinkId] Searching for linkId:', linkId);

    // NEW FORMAT: Try to get by document ID first (for new format where doc ID = link ID)
    if (linkId.startsWith('ALX-')) {
      const docRef = doc(db, PAYMENT_LINKS_COLLECTION, linkId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('[getPaymentLinkByLinkId] Found payment link by doc ID:', {
          id: docSnap.id,
          linkId: data.linkId,
          status: data.status,
        });
        return {
          id: docSnap.id,
          ...data,
        } as PaymentLink;
      }
    }

    // FALLBACK: Query by linkId field (for backward compatibility)
    const q = query(
      collection(db, PAYMENT_LINKS_COLLECTION),
      where('linkId', '==', linkId)
    );

    const querySnapshot = await getDocs(q);
    console.log('[getPaymentLinkByLinkId] Query result - empty:', querySnapshot.empty, 'size:', querySnapshot.size);

    if (querySnapshot.empty) {
      console.warn('[getPaymentLinkByLinkId] No payment link found for linkId:', linkId);
      return null;
    }

    const docData = querySnapshot.docs[0];
    const data = docData.data();
    console.log('[getPaymentLinkByLinkId] Found payment link by query:', {
      id: docData.id,
      linkId: data.linkId,
      status: data.status,
    });
    return {
      id: docData.id,
      ...data,
    } as PaymentLink;
  } catch (error) {
    console.error('Error getting payment link:', error);
    throw error;
  }
};

/**
 * Subscribe to realtime updates for a single payment link by linkId.
 * Useful for PaymentPage to auto-refresh when backend updates the payment.
 * Returns an unsubscribe function.
 */
export const subscribeToPaymentLinkByLinkId = (
  linkId: string,
  callback: (link: PaymentLink | null) => void
): (() => void) => {
  console.log('[subscribeToPaymentLinkByLinkId] Setting up listener for:', linkId);
  
  // For new format (ALX-...), use document ID directly
  if (linkId.startsWith('ALX-')) {
    const docRef = doc(db, PAYMENT_LINKS_COLLECTION, linkId);
    return onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('[subscribeToPaymentLinkByLinkId] Update received:', {
          linkId: data.linkId,
          totalPaid: data.totalPaid,
          remainingAmount: data.remainingAmount,
        });
        callback({
          id: docSnap.id,
          ...data,
        } as PaymentLink);
      } else {
        callback(null);
      }
    });
  }

  // Fallback: Query by linkId field
  const q = query(
    collection(db, PAYMENT_LINKS_COLLECTION),
    where('linkId', '==', linkId)
  );

  return onSnapshot(q, (querySnapshot) => {
    if (querySnapshot.empty) {
      callback(null);
      return;
    }
    const docData = querySnapshot.docs[0];
    const data = docData.data();
    console.log('[subscribeToPaymentLinkByLinkId] Update received (query):', {
      linkId: data.linkId,
      totalPaid: data.totalPaid,
      remainingAmount: data.remainingAmount,
    });
    callback({
      id: docData.id,
      ...data,
    } as PaymentLink);
  });
};

/**
 * Gets a payment link by document ID
 */
export const getPaymentLinkById = async (id: string): Promise<PaymentLink | null> => {
  try {
    const docRef = doc(db, PAYMENT_LINKS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as PaymentLink;
  } catch (error) {
    console.error('Error getting payment link by ID:', error);
    throw error;
  }
};

/**
 * Gets a payment link by ZiraatPay merchant payment ID
 * TODO: Used for payment callback verification
 */
export const getPaymentLinkByMerchantPaymentId = async (
  merchantPaymentId: string
): Promise<PaymentLink | null> => {
  try {
    const q = query(
      collection(db, PAYMENT_LINKS_COLLECTION),
      where('ziraatpayMerchantPaymentId', '==', merchantPaymentId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as PaymentLink;
  } catch (error) {
    console.error('Error getting payment link by merchant payment ID:', error);
    throw error;
  }
};

/**
 * Updates a payment link
 */
export const updatePaymentLink = async (id: string, updates: Partial<PaymentLink>): Promise<void> => {
  try {
    const docRef = doc(db, PAYMENT_LINKS_COLLECTION, id);

    // Clean data to remove any undefined values before updating Firestore
    const cleanUpdates = cleanFirestoreData({
      ...updates,
      updatedAt: Timestamp.now(),
    });

    await updateDoc(docRef, cleanUpdates);
  } catch (error) {
    console.error('Error updating payment link:', error);
    throw error;
  }
};

/**
 * Deletes a payment link
 */
export const deletePaymentLink = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, PAYMENT_LINKS_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting payment link:', error);
    throw error;
  }
};

/**
 * Gets all payment links with optional status filter
 * Sorted by lastPaymentUpdate (most recent payment first), falling back to createdAt
 * NOTE: We fetch by createdAt desc since not all records have lastPaymentUpdate,
 * then sort client-side to prioritize lastPaymentUpdate
 */
export const getAllPaymentLinks = async (statusFilter?: PaymentStatus): Promise<PaymentLink[]> => {
  try {
    let q = query(
      collection(db, PAYMENT_LINKS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    if (statusFilter) {
      q = query(
        collection(db, PAYMENT_LINKS_COLLECTION),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const links: PaymentLink[] = [];

    querySnapshot.forEach((doc) => {
      links.push({
        id: doc.id,
        ...doc.data(),
      } as PaymentLink);
    });

    // Sort by lastPaymentUpdate (desc), fallback to createdAt
    return links.sort((a, b) => {
      const aTime = a.lastPaymentUpdate?.toMillis() || a.createdAt?.toMillis() || 0;
      const bTime = b.lastPaymentUpdate?.toMillis() || b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });
  } catch (error) {
    console.error('Error getting payment links:', error);
    throw error;
  }
};

/**
 * Real-time listener for payment links
 * Sorted by lastPaymentUpdate (most recent payment first), falling back to createdAt
 * NOTE: Visit count updates do NOT affect lastPaymentUpdate, only payment events do
 */
export const subscribeToPaymentLinks = (
  callback: (links: PaymentLink[]) => void,
  statusFilter?: PaymentStatus
) => {
  let q = query(
    collection(db, PAYMENT_LINKS_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  if (statusFilter) {
    q = query(
      collection(db, PAYMENT_LINKS_COLLECTION),
      where('status', '==', statusFilter),
      orderBy('createdAt', 'desc')
    );
  }

  return onSnapshot(q, (querySnapshot) => {
    const links: PaymentLink[] = [];
    querySnapshot.forEach((doc) => {
      links.push({
        id: doc.id,
        ...doc.data(),
      } as PaymentLink);
    });
    // Sort by lastPaymentUpdate (desc), fallback to createdAt
    const sortedLinks = links.sort((a, b) => {
      const aTime = a.lastPaymentUpdate?.toMillis() || a.createdAt?.toMillis() || 0;
      const bTime = b.lastPaymentUpdate?.toMillis() || b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });
    callback(sortedLinks);
  });
};

/**
 * Marks a payment link as visited
 * NOTE: This function does NOT update the `updatedAt` field to avoid
 * polluting the "last update" timestamp with simple page visits.
 * Only payment events, errors, and manual updates should modify `updatedAt`.
 */
export const markPaymentLinkAsVisited = async (id: string): Promise<void> => {
  try {
    const paymentLink = await getPaymentLinkById(id);

    if (!paymentLink) {
      throw new Error('Payment link not found');
    }

    const updates: Partial<PaymentLink> = {
      lastVisitedAt: Timestamp.now(),
      visitCount: paymentLink.visitCount + 1,
    };

    // If status is still pending, update to visited
    if (paymentLink.status === 'pending') {
      updates.status = 'visited';
      updates.visitedAt = Timestamp.now();
    }

    // Direct Firestore update to avoid auto-updating `updatedAt`
    const docRef = doc(db, PAYMENT_LINKS_COLLECTION, id);
    const cleanUpdates = cleanFirestoreData(updates);
    await updateDoc(docRef, cleanUpdates);
  } catch (error) {
    console.error('Error marking payment link as visited:', error);
    throw error;
  }
};

/**
 * Checks if a payment link is expired
 */
export const isPaymentLinkExpired = (paymentLink: PaymentLink): boolean => {
  const now = new Date();
  const expiryDate = paymentLink.expiryDate.toDate();
  // Set both dates to start of day for comparison
  const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryDateOnly = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
  return nowDateOnly > expiryDateOnly;
};

/**
 * Creates a payment attempt record
 */
export const createPaymentAttempt = async (
  linkId: string,
  amount: number,
  status: AttemptStatus,
  cardLastFour?: string,
  cardType?: string,
  errorCode?: string,
  errorMessage?: string
): Promise<PaymentAttempt> => {
  try {
    // Get current attempt count for this link
    const attemptsQuery = query(
      collection(db, PAYMENT_ATTEMPTS_COLLECTION),
      where('linkId', '==', linkId)
    );
    const attemptsSnapshot = await getDocs(attemptsQuery);
    const attemptNumber = attemptsSnapshot.size + 1;

    const attemptData = {
      linkId,
      attemptNumber,
      timestamp: Timestamp.now(),
      amount,
      status,
      // Only include optional fields if they have values
      ...(cardLastFour && { cardLastFour }),
      ...(cardType && { cardType }),
      ...(errorCode && { errorCode }),
      ...(errorMessage && { errorMessage }),
    };

    // Clean data to remove any undefined values before saving to Firestore
    const cleanData = cleanFirestoreData(attemptData);

    const docRef = await addDoc(collection(db, PAYMENT_ATTEMPTS_COLLECTION), cleanData);

    return {
      id: docRef.id,
      ...attemptData,
    };
  } catch (error) {
    console.error('Error creating payment attempt:', error);
    throw error;
  }
};

/**
 * Gets all payment attempts for a link
 */
export const getPaymentAttempts = async (linkId: string): Promise<PaymentAttempt[]> => {
  try {
    const q = query(
      collection(db, PAYMENT_ATTEMPTS_COLLECTION),
      where('linkId', '==', linkId),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const attempts: PaymentAttempt[] = [];

    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data(),
      } as PaymentAttempt);
    });

    return attempts;
  } catch (error) {
    console.error('Error getting payment attempts:', error);
    throw error;
  }
};

/**
 * Updates payment link status after payment attempt
 */
export const updatePaymentStatus = async (
  paymentLinkId: string,
  status: PaymentStatus
): Promise<void> => {
  try {
    await updatePaymentLink(paymentLinkId, { status });
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};

/**
 * Processes a successful payment (partial or full)
 */
export const processPayment = async (
  paymentLinkId: string,
  amount: number,
  cardLastFour: string,
  cardType: string
): Promise<void> => {
  try {
    const paymentLink = await getPaymentLinkById(paymentLinkId);
    if (!paymentLink) {
      throw new Error('Payment link not found');
    }

    // Calculate new totals
    const newTotalPaid = paymentLink.totalPaid + amount;
    const newRemainingAmount = paymentLink.invoiceAmount - newTotalPaid;
    const newDepositPaid = newTotalPaid >= paymentLink.depositAmount;
    const newInvoicePaid = newRemainingAmount <= 0;

    // Determine payment type
    let paymentType: 'deposit' | 'partial' | 'full';
    if (newInvoicePaid) {
      paymentType = 'full';
    } else if (paymentLink.depositAmount > 0 && !paymentLink.depositPaid && newDepositPaid) {
      paymentType = 'deposit';
    } else {
      paymentType = 'partial';
    }

    // Create payment record
    const payment = {
      id: `PAY-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      amount,
      currency: paymentLink.currency,
      timestamp: Timestamp.now(),
      status: 'success' as const,
      paymentType,
      cardLastFour,
      cardType,
      transactionId: `TXN-${Date.now()}`,
    };

    // Determine new status
    let newStatus: PaymentStatus;
    if (newInvoicePaid) {
      newStatus = 'success';
    } else if (newTotalPaid > 0) {
      newStatus = 'partial';
    } else {
      newStatus = paymentLink.status;
    }

    // Debug logging
    console.log('processPayment - Status update:', {
      oldStatus: paymentLink.status,
      newStatus: newStatus,
      totalPaid: newTotalPaid,
      remainingAmount: newRemainingAmount,
      depositPaid: newDepositPaid,
      invoicePaid: newInvoicePaid,
      paymentType: paymentType
    });

    // Update payment link
    const updates: Partial<PaymentLink> = {
      totalPaid: newTotalPaid,
      remainingAmount: newRemainingAmount,
      depositPaid: newDepositPaid,
      invoicePaid: newInvoicePaid,
      payments: [...(paymentLink.payments || []), payment],
      status: newStatus,
    };

    await updatePaymentLink(paymentLinkId, updates);

    // Create payment attempt record
    await createPaymentAttempt(
      paymentLink.linkId,
      amount,
      'success',
      cardLastFour,
      cardType
    );
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};

/**
 * Updates invoice number for a payment link
 */
export const updateInvoiceNumber = async (
  paymentLinkId: string,
  invoiceNumber: string
): Promise<void> => {
  try {
    await updatePaymentLink(paymentLinkId, { invoiceNumber });
  } catch (error) {
    console.error('Error updating invoice number:', error);
    throw error;
  }
};

/**
 * Updates invoice and deposit amounts for a payment link
 */
export const updateInvoiceAmounts = async (
  paymentLinkId: string,
  invoiceAmount: number,
  depositAmount: number
): Promise<void> => {
  try {
    const paymentLink = await getPaymentLinkById(paymentLinkId);
    if (!paymentLink) {
      throw new Error('Payment link not found');
    }

    // Recalculate remaining amount
    const newRemainingAmount = invoiceAmount - paymentLink.totalPaid;

    const updates: Partial<PaymentLink> = {
      invoiceAmount,
      depositAmount,
      remainingAmount: newRemainingAmount,
      amount: invoiceAmount, // Backward compatibility
    };

    await updatePaymentLink(paymentLinkId, updates);
  } catch (error) {
    console.error('Error updating invoice amounts:', error);
    throw error;
  }
};

/**
 * Updates due dates for a payment link
 */
export const updateDueDates = async (
  paymentLinkId: string,
  depositDueDate: Date,
  invoiceDueDate: Date
): Promise<void> => {
  try {
    const updates: Partial<PaymentLink> = {
      depositDueDate: Timestamp.fromDate(depositDueDate),
      invoiceDueDate: Timestamp.fromDate(invoiceDueDate),
      expiryDate: Timestamp.fromDate(invoiceDueDate), // Update expiry to match invoice due
    };

    await updatePaymentLink(paymentLinkId, updates);
  } catch (error) {
    console.error('Error updating due dates:', error);
    throw error;
  }
};

/**
 * Checks for overdue payment links and updates their status
 */
export const checkAndUpdateOverdueLinks = async (): Promise<void> => {
  try {
    const allLinks = await getAllPaymentLinks();
    const now = new Date();

    for (const link of allLinks) {
      if (link.invoicePaid) {
        continue; // Skip fully paid invoices
      }

      let newStatus: PaymentStatus | null = null;

      // Check if deposit is overdue
      if (!link.depositPaid && link.depositDueDate.toDate() < now) {
        newStatus = 'overdue';
      }
      // Check if invoice is overdue
      else if (!link.invoicePaid && link.invoiceDueDate.toDate() < now) {
        newStatus = 'overdue';
      }

      // Update status if changed
      if (newStatus && link.status !== newStatus) {
        await updatePaymentStatus(link.id, newStatus);
      }
    }
  } catch (error) {
    console.error('Error checking overdue links:', error);
    throw error;
  }
};

/**
 * Searches payment links by customer name, link ID, or invoice number
 */
export const searchPaymentLinks = async (searchTerm: string): Promise<PaymentLink[]> => {
  try {
    // For now, get all links and filter in memory
    // In production, consider using Algolia or similar for better search
    const allLinks = await getAllPaymentLinks();

    const searchLower = searchTerm.toLowerCase().trim();

    return allLinks.filter(link => {
      const customerNameMatch = link.customerName.toLowerCase().includes(searchLower);
      const linkIdMatch = link.linkId.toLowerCase().includes(searchLower);
      const invoiceNumberMatch = link.invoiceNumber?.toLowerCase().includes(searchLower) || false;
      const emailMatch = link.customerEmail.toLowerCase().includes(searchLower);

      return customerNameMatch || linkIdMatch || invoiceNumberMatch || emailMatch;
    });
  } catch (error) {
    console.error('Error searching payment links:', error);
    throw error;
  }
};

/**
 * Adds a manual payment to a payment link
 * Used for recording cash, bank transfer, or other offline payments
 */
export const addManualPayment = async (input: AddManualPaymentInput): Promise<void> => {
  try {
    const functions = getFunctions();
    const addManualPaymentFn = httpsCallable(functions, 'addManualPayment');

    // Convert Date to ISO string for serialization
    const serializableInput = {
      ...input,
      paymentDate: input.paymentDate.toISOString(),
    };

    await addManualPaymentFn(serializableInput);
  } catch (error) {
    console.error('Error adding manual payment:', error);
    throw error;
  }
};

/**
 * Edits an existing manual payment entry
 * Only allowed for records created manually (not online card payments)
 */
export const editManualPayment = async (input: EditManualPaymentInput): Promise<void> => {
  try {
    const functions = getFunctions();
    const editManualPaymentFn = httpsCallable(functions, 'editManualPayment');

    const serializableInput = {
      ...input,
      paymentDate: input.paymentDate.toISOString(),
    };

    await editManualPaymentFn(serializableInput);
  } catch (error) {
    console.error('Error editing manual payment:', error);
    throw error;
  }
};

/**
 * Soft deletes a manual payment entry
 * Record is retained for audit purposes but excluded from totals
 */
export const softDeleteManualPayment = async (input: SoftDeleteManualPaymentInput): Promise<void> => {
  try {
    const functions = getFunctions();
    const softDeleteManualPaymentFn = httpsCallable(functions, 'softDeleteManualPayment');
    await softDeleteManualPaymentFn(input);
  } catch (error) {
    console.error('Error soft deleting manual payment:', error);
    throw error;
  }
};

/**
 * Loads the authoritative bank-attempt amount and reconciliation eligibility.
 */
export const getProcessingReconciliationDetails = async (
  paymentLinkId: string
): Promise<ProcessingReconciliationDetails> => {
  try {
    const functions = getFunctions();
    const detailsFn = httpsCallable<
      { paymentLinkId: string },
      ProcessingReconciliationDetails
    >(functions, 'getProcessingReconciliationDetails');
    const result = await detailsFn({ paymentLinkId });
    return result.data;
  } catch (error) {
    console.error('Error loading reconciliation details:', error);
    throw error;
  }
};

/**
 * Safely reconciles a stuck processing payment by calling backend guardrails.
 * The backend derives the amount from paymentAttempts; the client never supplies it.
 */
export const reconcileProcessingPayment = async (
  input: ReconcileProcessingPaymentInput
): Promise<void> => {
  try {
    const functions = getFunctions();
    const reconcileFn = httpsCallable(functions, 'reconcileProcessingPayment');

    const serializableInput = {
      ...input,
      paymentDate: input.paymentDate.toISOString(),
    };

    await reconcileFn(serializableInput);
  } catch (error) {
    console.error('Error reconciling processing payment:', error);
    throw error;
  }
};
